import LeftArrow from "@assets/svg/arrow-left.svg?react";
import AutomationMobilePhoto from "@assets/svg/AutomationMobilePhoto.svg?react";
import { LoadingWrapper } from "@components";
import { ToastMessageContext } from "@context/toast-message-cotnext";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useAutomation } from "@services";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import Benefits from "./Benefits";

interface Benefit {
  title: string;
  description: string;
}

interface BookingConfirmationProps {
  onclose: () => void;
  mainInfoTitle?: string;
  mainInfoDescription?: string;
  generalOverviewTitle?: string;
  generalOverviewDescription?: string;
  benefitsTitle?: string;
  benefitsDescription?: string;
  benefits?: Benefit[];
  refetch: () => void;
  id: any;
  openAfterConfirmation: () => void;
}

const BookingConfirmation = ({
  onclose,
  mainInfoTitle,
  mainInfoDescription,
  generalOverviewTitle,
  generalOverviewDescription,
  benefitsTitle,
  benefitsDescription,
  benefits,
  refetch,
  openAfterConfirmation,
  id,
}: BookingConfirmationProps) => {
  const { showError, showSuccess } = useContext(ToastMessageContext);

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === "ar";
  const { SetOnboardingCompleteMutate } = useAutomation();
  const { mutate, isPending } = SetOnboardingCompleteMutate({
    onSuccess: () => {
      onclose();
      openAfterConfirmation();
      refetch();
    },
    onError: (error) => {
      showError(t("ERRORS.SOMETHING_WENT_WRONG"));
    },
  });
  return (
    <div
      style={{
        direction: isRtl ? "rtl" : "ltr",
        borderRight: isRtl ? "1px solid rgba(234, 237, 241, 1)" : "none",
        borderLeft: !isRtl ? "1px solid rgba(234, 237, 241, 1)" : "none",
        backgroundColor: "#FFFFFF",
        height: "100%",
        width: "570px",
        position: "absolute",
        top: "0",
        bottom: "0",
        left: isRtl ? "0" : "auto",
        right: isRtl ? "auto" : "0",
        zIndex: "1000",
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        gap: "24px",
        paddingInline: "20px",
      }}
    >
      <span
        style={{
          display: isPending ? "flex" : "none",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <CircularProgress />
      </span>
      <span
        onClick={onclose}
        style={{
          width: "24px",
          height: "24px",
          cursor: "pointer",
          left: "24px",
          top: "24px",
          position: "absolute",
          zIndex: "102", 
        }}
      >
        <LeftArrow />
      </span>
      {/* Title section  */}
      <Box
        sx={{
          width: "522px",
          height: "37px",
          display: "flex",
          alignItems: "start",
          justifyContent: "space-between",
          marginTop: "72px",
        }}
      >
        <span>
          <Typography
            sx={{ fontWeight: 700, fontSize: "18px", color: "#130080" }}
          >
            {mainInfoTitle}
          </Typography>
        </span>
        <Button
          onClick={() => {
            mutate({ id: id });
          }}
          sx={{
            width: "76px",
            height: "30px",
            gap: "10px",
            borderRadius: "12px",
            paddingTop: "12px",
            paddingRight: "16px",
            paddingBottom: "12px",
            paddingLeft: "16px",
            backgroundColor: "#5B3FFF",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#5B3FFF",
              opacity: 0.8,
            },
          }}
        >
          {t("Automation.activate")}
        </Button>
      </Box>

      {/* Hero section  */}
      <Box
        sx={{
          width: "522px",
          height: "145.33px",
          padding: "24px",
          gap: "120px",
          borderRadius: "12px",
          backgroundImage: !isRtl
            ? "linear-gradient(to right, #130080 50%, #5B3FFF 70%)"
            : "linear-gradient(to left, #130080 50%, #5B3FFF 70%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <span style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Typography
            sx={{
              width: "auto",
              height: "24px",
              fontWeight: 700,
              fontSize: "24px",
              lineHeight: "24px",
              color: "#F5F5F5",
            }}
          >
            {mainInfoTitle}
          </Typography>
          <Typography
            sx={{
              width: !isRtl? "322px" : "248px",
              height: "48px",
              fontWeight: 500,
              fontSize: !isRtl ? "12px" : "14px",
              lineHeight: "24px",
              color: "#F5F5F5",
            }}
          >
            {mainInfoDescription}
          </Typography>
        </span>
        <AutomationMobilePhoto
          style={{
            width: "187px",
            height: "187px",
            position: "absolute",
            left: isRtl ? "5px" : "unset",
            right: isRtl ? "unset" : "5px",
          }}
        />
      </Box>
      {/* Overview section  */}
      <Box
        sx={{
          width: "436px",
          height: "auto",
          gap: "16px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          sx={{
            width: "fit-content",
            height: "10px",
            fontWeight: 700,
            fontSize: "16px",
            color: "#625783",
          }}
        >
          {generalOverviewTitle}
        </Typography>
        <Typography
          sx={{
            width: "436px",
            height: "auto",
            fontWeight: 400,
            fontSize: "12px",
            lineHeight: "14.4px",
            color: "#435363",
            paddingTop: "16px",
          }}
        >
          {generalOverviewDescription}
        </Typography>
      </Box>
      {/* Main Benefits Section */}
      <Benefits
        title={benefitsTitle}
        subtitle={benefitsDescription}
        benefits={benefits} 
      />
    </div>
  );
};

export default BookingConfirmation;
